import { Button, Stack, TextInput, Text } from "@mantine/core";
import { FC, useState } from "react";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { resendCode } from "../../api/userApi";
import useAuthStore from "../../store/useAuthStore";
import { setProfileLanguage } from "../../api/profileApi";
import useProfileStore from "../../store/useProfileStore";

interface Props {
  onContinue: () => void;
}

export const CodeVerificationForm: FC<Props> = ({ onContinue }) => {
  const { user, verifyEmail } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const { getProfileLanguage } = useProfileStore();

  const form = useForm({
    initialValues: {
      code: "",
    },

    validate: {
      code: (value) => (value.trim().length === 0 ? t("code_required") : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (!form.validate().hasErrors) {
      setLoading(true);
      if (user?.id) {
        try {
          const response = await verifyEmail({
            code: values.code,
            user_id: user.id,
          });
          if (response.status === "success") {
            await setProfileLanguage(i18n.language);
            getProfileLanguage();

            onContinue();
            setLoading(false);
          } else {
            setError(t("incorrect_code"));
            setLoading(false);
          }
        } catch (err) {
          setError(t("incorrect_code"));
          setLoading(false);
        }
      }
    }
  };

  const handleResendCode = async () => {
    if (user?.id) {
      setIsResending(true);
      setError(null);
      setSuccessMessage(null);

      try {
        await resendCode({ user_id: user.id });
        setSuccessMessage(t("code_sent_successfully"));
      } catch (err) {
        setError(t("resend_code_failed"));
      } finally {
        setIsResending(false);
      }
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack align="stretch" justify="center" gap="24px">
        <TextInput
          placeholder={t("verification_code_placeholder")}
          label={t("verification_code_label")}
          {...form.getInputProps("code")}
          onChange={(event) => {
            form.setFieldValue("code", event.currentTarget.value);
            setSuccessMessage(null);
          }}
        />
        {error && <Text color="red">{error}</Text>}
        {successMessage && <Text color="green">{successMessage}</Text>}
        <Button fullWidth type="submit" loading={loading}>
          {t("forgot_password.continue_button")}
        </Button>

        <Button
          fullWidth
          variant="subtle"
          onClick={handleResendCode}
          disabled={isResending}
        >
          {isResending ? t("sending_code") : t("resend_code_button")}
        </Button>
      </Stack>
    </form>
  );
};
